/**
 * WHAT THE SCREEN IS ALLOWED TO OFFER — the six ladders, the lifts with a one-rep max, the
 * movements whose unbroken set is counted, and the body parts.
 *
 * 🚨 THIS FILE IS A MIRROR, AND NOTHING EXECUTABLE KEEPS IT IN STEP. Read this before
 * editing it.
 *
 * The identifiers below belong to the engine, which lives at `api/src/engine/vocabulary/`.
 * The app cannot import them: `src/` may not reach into `api/`, and `shared/contracts/
 * athlete.ts` deliberately types `ladder`, `movement` and `bodyPart` as OPEN strings — its
 * own comment says a contract stricter than the column silently deletes rows the database
 * is happy to hold. So the wire says nothing about which ladders exist, and no endpoint
 * publishes the registry.
 *
 * That leaves a hand-written copy as the only way to draw the screen, and the cost is
 * stated rather than hidden: **add a ladder or a rung to the engine and this file does not
 * notice.** Every value here was read out of the engine on 2026-08-28 and is cited below
 * with the file it came from, so the next person can diff rather than guess. A published
 * registry (one `GET /v1/meta/vocabulary`, or the vocabulary moved into `shared/`) is what
 * actually fixes it; see the PR.
 *
 * ---
 *
 * ## THE SIX LADDERS, AND THE NAME AN ATHLETE CALLS EACH ONE
 *
 * The design shows TWO ladder cards. The engine defines SIX
 * (`api/src/engine/vocabulary/ladders.ts` — verified, six entries). The design is
 * illustrating the pattern, not capping it, so all six are built.
 *
 * The engine names a ladder after the PATTERN it orders — `pull`, `handstand`, `midline`,
 * `squat`, `rope`, `olympic`. Nobody says "I am on the midline ladder".
 *
 * ⚠️ A LADDER'S NAME IS NO LONGER A MOVEMENT ID, AND THAT IS A CORRECTION. Each ladder used
 * to be titled with one of its own rungs — the TOP one, mostly — so the six headings read
 * "Handstand Push-Up", "Pull-Up", "Toes-to-Bar", "Pistol", "Double-Under", "Snatch". Two
 * things were wrong with that. A heading naming the top rung tells an athlete standing on
 * rung zero that the list is about something they cannot do; and the heading was then the
 * same string as one of the rows underneath it, which reads as a duplicate rather than as a
 * group. The owner's names are the family, not the summit:
 *
 * | engine id   | engine's own name | shown to the athlete as |
 * | ----------- | ----------------- | ----------------------- |
 * | `handstand` | Handstand         | Push-Ups                |
 * | `pull`      | Pulling           | Pull-Ups                |
 * | `midline`   | Midline           | 2TB                     |
 * | `squat`     | Squat             | Squats                  |
 * | `rope`      | Jump rope         | Jump Rope               |
 * | `olympic`   | Barbell           | Olympic Weightlifting   |
 *
 * The words themselves live in `locales/{en,de}/athlete.json` under `ladders.name.<id>`, and
 * the LADDER ID is the key — so this file owns the STRUCTURE (which ladders, which rungs, in
 * which order) and the locale files own every word on the screen, which is the same division
 * the rungs already had.
 *
 * ## HOW THE RUNG COUNTS WERE DERIVED
 *
 * A rung is a number on `athlete_ladders.rung`, and the engine's catalog assigns one to
 * each movement (`movements.ts`, `ladder` + `rung`). Several movements can share a rung —
 * `box-hspu` and `wall-walk` are both handstand rung 2, `back-squat` and `front-squat` are
 * both squat rung 1 — so the ladder an athlete taps has one entry per DISTINCT rung, named
 * after the movement that rung is recognised by, not one entry per movement.
 *
 * ⚠️ `rope` IS THE ONE LADDER WITH NO RUNG ZERO IN THE CATALOG: its movements are
 * `single-under` at 1 and `double-under` at 2. The contract says "rung zero is the bottom
 * of every ladder and is what the population floor assumes", and `canReach` reads a missing
 * ladder as rung 0 — so rung 0 exists whether or not a movement sits on it. It is offered
 * here as a real, tappable rung meaning "not skipping yet", because an athlete who cannot
 * yet do a single-under has to be able to say so.
 */

/** The six ladder ids, exactly as `athlete_ladders.ladder` stores them. */
export const LADDER_IDS = ["handstand", "pull", "midline", "squat", "rope", "olympic"] as const;

export type LadderId = (typeof LADDER_IDS)[number];

export interface LadderSpec {
  /**
   * ⚠️ ALSO THE TRANSLATION KEY FOR THE HEADING — `ladders.name.<id>`. A ladder no longer
   * carries a `title` naming one of its own rungs; see the table above for why.
   */
  id: LadderId;
  /**
   * One entry per DISTINCT rung, ordered from the bottom, each naming the movement that
   * rung is recognised by. `null` is a rung the catalog puts no movement on — see the note
   * about `rope` above — and renders as the generic floor label.
   *
   * The index IS the rung number written to `athlete_ladders.rung`. Nothing derives it from
   * a lookup, so a rung cannot be off by one between what is drawn and what is stored.
   */
  rungs: readonly (string | null)[];
}

/**
 * ORDERED AS THE SCREEN DRAWS THEM: the two the design shows first, then the rest.
 *
 * Movements sharing a rung are named in the comment beside it — only one of them can be the
 * label, and it is the one the rung is recognised by.
 */
export const LADDERS: readonly LadderSpec[] = [
  {
    id: "handstand",
    /* rung 2 is also `wall-walk` */
    rungs: ["push-up", "pike-push-up", "box-hspu", "hspu", "strict-hspu"],
  },
  {
    id: "pull",
    rungs: [
      "ring-row",
      "jumping-pull-up",
      "strict-pull-up",
      "pull-up",
      "chest-to-bar",
      "bar-muscle-up",
      "ring-muscle-up",
    ],
  },
  {
    id: "midline",
    rungs: ["sit-up", "hanging-knee-raise", "knees-to-elbow", "toes-to-bar"],
  },
  {
    id: "squat",
    /* rung 0 is also `goblet-squat`; rung 1 is also `front-squat` */
    rungs: ["air-squat", "back-squat", "overhead-squat", "pistol"],
  },
  {
    id: "rope",
    /* rung 0 carries no movement in the catalog — see the note above */
    rungs: [null, "single-under", "double-under"],
  },
  {
    id: "olympic",
    /* rung 0 is also `sumo-deadlift`; rung 2 also `kb-clean`; rung 3 also `clean-and-jerk`
       and `push-jerk`; rung 4 also `db-snatch` */
    rungs: ["deadlift", "hang-power-clean", "power-clean", "clean", "power-snatch", "snatch"],
  },
];

/**
 * WHICH LADDER RUNG A TESTABLE LIFT SITS ON — the gate on "which maxes are worth asking for".
 *
 * 🚨 A MAX FOR A LIFT THE ATHLETE CANNOT DO IS A NUMBER THEY WILL INVENT. First run used to
 * offer all twelve boxes to everybody, so somebody who had just placed themselves at the
 * bottom of the barbell ladder was asked for their snatch on the very next screen. The rungs
 * they just tapped already answer that, so the step asks only for the lifts they have
 * reached.
 *
 * ⚠️ IT INCLUDES THE MOVEMENTS THAT SHARE A RUNG, which `LADDERS` above can only note in a
 * comment: a ladder draws ONE row per distinct rung, so `sumo-deadlift`, `front-squat` and
 * `clean-and-jerk` are not row labels even though the engine puts each of them on a rung. A
 * lift absent from this table is one no ladder orders — a strict press, a thruster, a bench —
 * and it is always offered, because there is no rung to be short of.
 *
 * Read out of the engine's own catalog (`api/src/engine/vocabulary/movements.ts`, `ladder` +
 * `rung`) with the rest of this file, and carrying the same warning: nothing keeps it in step.
 */
export const LIFT_RUNGS: Readonly<Record<string, { ladder: LadderId; rung: number }>> = {
  deadlift: { ladder: "olympic", rung: 0 },
  "sumo-deadlift": { ladder: "olympic", rung: 0 },
  "power-clean": { ladder: "olympic", rung: 2 },
  clean: { ladder: "olympic", rung: 3 },
  "clean-and-jerk": { ladder: "olympic", rung: 3 },
  snatch: { ladder: "olympic", rung: 5 },
  "back-squat": { ladder: "squat", rung: 1 },
  "front-squat": { ladder: "squat", rung: 1 },
  "overhead-squat": { ladder: "squat", rung: 2 },
};

/**
 * THE LIFTS YOU RECORD A ONE-REP MAX FOR — the twelve keys of `referenceMaxes` in
 * `api/src/engine/capability/strength.ts`, in the order that file writes them.
 *
 * This is not a judgement call made here: that table's own comment says "IT IS ALSO THE
 * LIST OF LIFTS YOU WOULD RECORD A ONE-REP MAX FOR", and `isTestable()` is defined as
 * membership in it — a lift outside it is one the engine will not build to a heavy single
 * on, so offering a box to type a max into would be offering to record a number nothing
 * reads.
 */
export const TESTABLE_LIFTS = [
  "deadlift",
  "back-squat",
  "front-squat",
  "strict-press",
  "clean",
  "power-clean",
  "snatch",
  "clean-and-jerk",
  "thruster",
  "overhead-squat",
  "sumo-deadlift",
  "bench-press",
] as const;

/**
 * THE MOVEMENTS WHOSE UNBROKEN SET IS COUNTED — every rung movement of the five ladders the
 * engine marks `capacityIsMeasured: true`, which is all of them except `olympic`.
 *
 * The distinction is the engine's own and it is the reason this list is not "the gymnastics
 * movements": how many you can string together derives from nothing, so on these ladders it
 * is measured. A one-rep max derives from another lift, which is why the barbell ladder is
 * `capacityIsMeasured: false` and why none of its movements is here.
 *
 * Ordered by ladder and then by rung, so the section reads bottom-to-top of each
 * progression rather than alphabetically.
 */
export const MEASURED_SKILLS = [
  /* handstand */
  "push-up",
  "pike-push-up",
  "box-hspu",
  "wall-walk",
  "hspu",
  "strict-hspu",
  /* pull */
  "ring-row",
  "jumping-pull-up",
  "strict-pull-up",
  "pull-up",
  "chest-to-bar",
  "bar-muscle-up",
  "ring-muscle-up",
  /* midline */
  "sit-up",
  "hanging-knee-raise",
  "knees-to-elbow",
  "toes-to-bar",
  /* squat */
  "air-squat",
  "goblet-squat",
  "back-squat",
  "front-squat",
  "overhead-squat",
  "pistol",
  /* rope */
  "single-under",
  "double-under",
] as const;

/**
 * THE BODY MAP — the thirteen keys of `bodyParts` in `api/src/engine/vocabulary/body.ts`,
 * in that file's own head-to-toe `order`.
 *
 * ⚠️ THE SET IS THE ENGINE'S, NOT A PERSON'S IDEA OF A BODY. "Knee" is not in it: the
 * engine has `quads`, `hamstrings` and `calves`, and a bad knee is deliberately NOT a quads
 * restriction — `shared/contracts/athlete.ts` says so on the target union, because a back
 * squat and a box jump name the same parts and are not the same problem. That is what the
 * impact target is for, and it is offered beside these.
 */
export const BODY_PARTS = [
  "wrists",
  "shoulders",
  "arms",
  "chest",
  "back",
  "spine",
  "midline",
  "hips",
  "glutes",
  "hamstrings",
  "quads",
  "calves",
  "ankles",
] as const;

/**
 * EVERY MOVEMENT THIS SCREEN CAN NAME — the two lists above, deduplicated, lifts first.
 *
 * Used only by the restriction editor, where the target may be a MOVEMENT rather than a
 * body part: "not this one, for now". Offering the whole engine catalog there would be
 * offering ninety rows including warm-up drills and stretches; these are the movements the
 * athlete has already been asked about elsewhere on this screen, which is the set they can
 * recognise.
 */
export const NAMEABLE_MOVEMENTS: readonly string[] = [
  ...TESTABLE_LIFTS,
  ...MEASURED_SKILLS.filter((movement) => !TESTABLE_LIFTS.includes(movement as never)),
];

/**
 * THE FOUR PATTERNS A `limit-range` RESTRICTION REMOVES — the ones the engine marks
 * `fullRange: true` in `api/src/engine/vocabulary/patterns.ts`, which is its own comment's
 * definition: "overhead is the end of a shoulder, a squat the bottom of a hip and a knee, a
 * hinge the end of a hamstring, and an olympic lift passes through all of them under
 * speed."
 *
 * They are here because the restrictions list draws them as chips — what is being worked
 * around, in the engine's own vocabulary, rather than a sentence about an injury.
 */
export const FULL_RANGE_PATTERNS = ["overhead", "squat", "hinge", "olympic"] as const;
